import React from "react";

// Small state machine for auth forms. Pages provide the initial field set
// and get back:
//   fields        — current field values
//   setField(key, value)  — setter
//   errors        — { field: [msgs] } + `base`
//   setErrors(next)       — setter (merge)
//   clearError(key)       — clear a single field's error
//   submitting    — boolean
//   setSubmitting — setter
//
// Kept intentionally small — page components own the actual submit logic
// (apiPost, router.visit, etc.) since each page has a different response
// shape to handle.
export default function useAuthForm(initialFields = {}) {
  const [fields, setFields] = React.useState(initialFields);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  const setField = React.useCallback((key, value) => {
    setFields((f) => ({ ...f, [key]: value }));
    // Auto-clear the field-specific error when the user edits — feels live.
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearError = React.useCallback((key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const mergeErrors = React.useCallback((next) => {
    setErrors((prev) => ({ ...prev, ...next }));
  }, []);

  return {
    fields, setFields, setField,
    errors, setErrors, mergeErrors, clearError,
    submitting, setSubmitting,
  };
}

// Helper: normalize server-side error hashes (which may be arrays OR strings)
// into { field: 'first message' } so components can render a single inline
// error per field.
export function normalizeServerErrors(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      out[key] = val[0];
    } else if (typeof val === "string") {
      out[key] = val;
    }
  });
  return out;
}
